import { Link } from 'react-router-dom';
import { ArrowRight, FileText, Map, Route } from 'lucide-react';
import SEO, { type Crumb } from '@/components/SEO';
import PageHeader from '@/components/PageHeader';
import EngagementLinks from '@/components/EngagementLinks';
import { staticPage } from '@/lib/content';
import { AUTHOR } from '@/lib/site';
import './estate-review.css';

const DELIVERABLES = [
  { title: 'Component and dependency map', body: 'The products, versions, domains, storage and shared services in scope, connected to the business processes they support.' },
  { title: 'Customizations and integrations', body: 'The rules, extensions, document links and interfaces that need to be retained, changed or investigated, with ownership and evidence gaps recorded.' },
  { title: 'Lifecycle and readiness findings', body: 'Release-specific support and compatibility questions, operational concerns and the evidence needed to resolve them. Verified facts are separated from assumptions.' },
  { title: 'Options and tradeoffs', body: 'Practical choices for the agreed scope: retain and maintain, upgrade, change hosting, or replace a defined workload. Dependencies and unresolved conditions accompany each option.' },
  { title: 'Recommended sequence', body: 'An ordered plan of decisions, prerequisite work, rehearsal and acceptance checks. Where evidence is missing, the next step is to resolve it before committing to a delivery path.' },
  { title: 'Written pack and review meeting', body: 'A findings and decision pack your team can use, plus a meeting to walk through the evidence, priorities, open questions and proposed owners.' },
];

const INPUTS = [
  { title: 'Start with a conversation', body: 'Describe the process, the change you are considering and the decision you need to make. The first discussion needs no access to your systems.' },
  { title: 'Use redacted evidence', body: 'Your team can supply an agreed inventory, diagram or configuration summary with credentials, personal information and customer documents removed.' },
  { title: 'Agree any collection or access', body: 'If more evidence is needed, agree the method, permissions, handling and boundaries in advance. Collection can be performed by your team or through an agreed access arrangement.' },
];

export default function EstateReview() {
  const page = staticPage('/estate-review');
  const crumbs: Crumb[] = [{ name: 'Home', href: '/' }, { name: 'Estate Review', href: '/estate-review' }];
  return (
    <div className="estate-page">
      <SEO title={page.title} description={page.description} path="/estate-review" breadcrumbs={crumbs} />
      <PageHeader
        crumbs={crumbs}
        label="WebCenter Estate Review"
        title="A clear decision starts with the estate you have."
        lead="Understand the components, customizations and connections behind your WebCenter process. Leave with a written assessment of what needs attention, which options fit and what to do next."
      >
        <div className="mt-8 flex flex-wrap gap-4">
          <Link to="/contact?topic=estate-review" className="btn-forest">Discuss an Estate Review <ArrowRight size={18} aria-hidden="true" /></Link>
          <Link to="/estate-review/sample" className="btn-outline-forest">Inspect a sample pack <FileText size={18} aria-hidden="true" /></Link>
        </div>
      </PageHeader>
      <EngagementLinks current="/estate-review" />

      <section className="container py-14 md:py-16" aria-labelledby="review-fit-heading">
        <div className="estate-split">
          <div>
            <p className="label">The decision in front of you</p>
            <h2 id="review-fit-heading" className="mt-4 text-subhead">Know what must keep working.</h2>
            <p className="mt-5 text-body">An upgrade, an ERP change or a recurring operational problem can begin with the same uncertainty: which parts of the estate depend on each other, and what will a change affect?</p>
            <p className="mt-4 text-body">The review brings the technical picture and the business requirement together. It is useful when you need a defensible starting point before choosing an implementation, or a clearer scope for work already being discussed.</p>
          </div>
          <aside className="estate-note" aria-labelledby="review-owner-heading">
            <p className="label">Named review lead</p>
            <h3 id="review-owner-heading" className="mt-3 text-subhead">{AUTHOR.name}</h3>
            <p className="mt-4 text-body">Andrew leads the discussion of the WebCenter estate, its application process and the dependencies around it.</p>
            <Link to="/about" className="mt-5 inline-block link">Read Andrew’s background</Link>
            <div className="mt-6 border-t border-rule pt-5">
              <p className="text-small">A fixed scope is agreed in writing after the initial conversation. The proposal defines the questions, inputs, deliverables, responsibilities and commercial terms.</p>
            </div>
          </aside>
        </div>
      </section>

      <section className="border-y border-rule bg-cream-light" aria-labelledby="review-deliverables-heading">
        <div className="container py-14 md:py-16">
          <p className="label">What your team receives</p>
          <h2 id="review-deliverables-heading" className="mt-4 max-w-3xl text-subhead">A pack you can read, challenge and use.</h2>
          <ol className="estate-deliverables mt-9">
            {DELIVERABLES.map((item, index) => (
              <li key={item.title}>
                <span className="label" aria-hidden="true">0{index + 1}</span>
                <h3 className="mt-3 text-lead">{item.title}</h3>
                <p className="mt-3 text-body">{item.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="container py-14 md:py-16" aria-labelledby="review-inputs-heading">
        <div className="estate-split">
          <div>
            <p className="label">Inputs and access</p>
            <h2 id="review-inputs-heading" className="mt-4 text-subhead">Begin with what you can share.</h2>
            <p className="mt-5 text-body">A complete inventory is useful, but you do not need one to begin. Agree the review questions first, then collect the evidence those questions require.</p>
          </div>
          <ol className="estate-inputs">
            {INPUTS.map((item, index) => (
              <li key={item.title}>
                <span className="label" aria-hidden="true">0{index + 1}</span>
                <div><h3 className="text-small">{item.title}</h3><p className="mt-2 text-body">{item.body}</p></div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="border-y border-rule bg-cream-light" aria-labelledby="review-boundaries-heading">
        <div className="container py-14 md:py-16 estate-split">
          <div>
            <p className="label">Scope and boundaries</p>
            <h2 id="review-boundaries-heading" className="mt-4 text-subhead">Make the limits visible.</h2>
            <p className="mt-5 text-body">The review evaluates the evidence available within the agreed scope. Unknown versions, inaccessible integrations and untested recovery procedures stay visible in the findings and recommended sequence.</p>
          </div>
          <div className="space-y-5 text-body">
            <p>Implementation, production changes, penetration testing and a formal compliance assessment require separate scope. A review does not certify an environment or establish a supported product combination without the relevant checks.</p>
            <p>Validation activities and any inspection tool are agreed in advance. You keep the written pack and can use it to decide how to proceed with your own team or a delivery partner.</p>
            <p>Please use the contact form for a short description of the decision. Detailed exports, credentials and customer documents should follow an agreed handling process.</p>
          </div>
        </div>
      </section>

      <section className="container py-14 md:py-16" aria-labelledby="review-sample-heading">
        <div className="estate-sample-teaser">
          <div className="estate-teaser-marks" aria-hidden="true"><Map size={40} /><ArrowRight size={22} /><FileText size={40} /><ArrowRight size={22} /><Route size={40} /></div>
          <div>
            <p className="label">See the shape of the work</p>
            <h2 id="review-sample-heading" className="mt-3 text-subhead">Inspect an illustrative decision pack.</h2>
            <p className="mt-4 text-body">The sample uses a fictional environment to show how a dependency map, findings and a recommended sequence fit together. It contains no customer information or claimed project results.</p>
            <Link to="/estate-review/sample" className="mt-5 inline-flex items-center gap-2 link">Open the sample pack <ArrowRight size={18} aria-hidden="true" /></Link>
          </div>
        </div>
        <div className="mt-12 border-t border-rule pt-9">
          <h2 className="text-subhead">What decision does your team need to make?</h2>
          <p className="mt-4 max-w-3xl text-body">Tell us what is running, what needs to change and which constraints matter. We will use the first conversation to define a useful review scope.</p>
          <Link to="/contact?topic=estate-review" className="mt-6 btn-forest">Discuss your estate <ArrowRight size={18} aria-hidden="true" /></Link>
        </div>
      </section>
    </div>
  );
}
